/**
 * CampaignFormCta
 * CTA click → write FormTargetValue into Campaign Form Target hidden field → smooth-scroll to href anchor.
 *
 * CMS setup:
 * 1. Form includes a "Campaign Form Target (Hidden)" element (content name e.g. TargetEmail).
 * 2. Email actor To = ::TargetEmail:: (Insert placeholder).
 * 3. Each CTA: URL = #form-anchor, Form target value = recipient email (or key).
 * 4. Wrap/give the form container id matching the CTA hash (e.g. id="campaign-form").
 */
import { devLogger } from "@/utils/dev-logger.util";
const FORM_TARGET_SELECTOR = "input[data-form-target], [data-form-target]";
function resolveScrollTarget(href, formInput) {
    if (href?.includes("#")) {
        const hashIndex = href.indexOf("#");
        const id = decodeURIComponent(href.slice(hashIndex + 1)).split("?")[0].trim();
        if (id) {
            const byId = document.getElementById(id);
            if (byId) {
                return byId;
            }
        }
    }
    // Fallback when TinyMCE strips empty <div id="..."> anchors: scroll to the form itself
    if (formInput) {
        const form = formInput.closest("form, .EPiServerForms, [data-f-type='form']") ?? formInput;
        return form;
    }
    return null;
}
function findFormTargetInput(scrollTarget) {
    const scopes = [];
    if (scrollTarget) {
        scopes.push(scrollTarget);
        const form = scrollTarget.closest("form, .EPiServerForms, [data-f-type='form']");
        if (form) {
            scopes.push(form);
        }
    }
    scopes.push(document);
    for (const scope of scopes) {
        const candidate = scope.querySelector(FORM_TARGET_SELECTOR);
        if (candidate instanceof HTMLInputElement) {
            return candidate;
        }
        if (candidate instanceof HTMLElement) {
            const nested = candidate.querySelector("input");
            if (nested) {
                return nested;
            }
        }
    }
    return null;
}
function setFormTargetValue(input, value) {
    input.value = value;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
}
function scrollToTarget(target) {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
}
export const CampaignFormCta = (element) => {
    const value = element.getAttribute("data-form-target-value")?.trim();
    if (!value) {
        return;
    }
    const handleClick = (event) => {
        const href = element.getAttribute("href") ?? "";
        const input = findFormTargetInput(null);
        if (!input) {
            devLogger.warn("CampaignFormCta: No Campaign Form Target hidden field found (input[data-form-target]).");
            // Still allow native hash scroll if present
            return;
        }
        event.preventDefault();
        setFormTargetValue(input, value);
        const scrollTarget = resolveScrollTarget(href, input);
        if (scrollTarget) {
            scrollToTarget(scrollTarget);
            const firstField = scrollTarget.querySelector("input:not([type='hidden']):not([type='submit']), textarea, select");
            firstField?.focus({ preventScroll: true });
        }
    };
    element.addEventListener("click", handleClick);
    return () => {
        element.removeEventListener("click", handleClick);
    };
};
export default CampaignFormCta;
